import {
  AssessmentQuestionType,
  GetReviewStepQuestionsQuery,
  ReviewStepQuestionsFragment,
} from "types/graphql-schema";

import { assertEdgesNonNull } from "@helpers/helpers";

import { PreviewReviewChanges } from "../steps/review-preview-questions";

/**
 * The questions of one published review step, as its Questions tab lists them (TF-1697).
 *
 * The backend sends each question set the way the library stores it, plus this review's
 * own changes: a `removed` flag on the set's link, and one override per hidden or reworded
 * question. These helpers fold the two into rows, so a removed section or question is
 * still listed (with "Show again") and a reworded question shows this review's wording.
 */

/** A published review step whose questions can still change. */
export type ReviewQuestionScope = {
  complianceProgramId: number;
  assessmentTemplateId: number;
};

/** The scope of a step's question changes, or null when the review is not published. */
export const getReviewQuestionScope = (
  publishedReviewId: number | null,
  step: { templateId?: number } | undefined,
): ReviewQuestionScope | null =>
  publishedReviewId && step?.templateId
    ? {
        complianceProgramId: publishedReviewId,
        assessmentTemplateId: step.templateId,
      }
    : null;

type ReviewQuestionSetLink =
  ReviewStepQuestionsFragment["questionSetLinks"][number];
type ReviewQuestionOverride =
  ReviewStepQuestionsFragment["questionOverrides"][number];

export type ReviewQuestionRow = {
  questionId: number;
  /** The wording this review shows: its own when it has one, else the library's. */
  title: string;
  description: string | null;
  /** True when the question is a talent indicator: those are never reworded. */
  isTalentIndicator: boolean;
  removed: boolean;
  /** True when this review has its own title or description for the question. */
  changed: boolean;
};

type ReviewSectionRow = {
  sectionId: number;
  title: string | null;
  description: string | null;
  /** The whole set was removed, or every question of the section is hidden. */
  removed: boolean;
  questions: ReviewQuestionRow[];
};

/** The step's template, found among the review's templates by id. */
export const findReviewStepTemplate = (
  complianceProgram: GetReviewStepQuestionsQuery["complianceProgram"],
  assessmentTemplateId: number,
) =>
  [
    complianceProgram?.performanceAssessmentTemplate,
    complianceProgram?.managerAssessmentTemplate,
    complianceProgram?.peerAssessmentTemplate,
    complianceProgram?.engagementSurveyTemplate,
  ].find((template) => template?.id === assessmentTemplateId) ?? null;

/** Which of the review's template fields holds this step, for queries asked through it. */
export const reviewStepTemplateFlags = (
  complianceProgram: GetReviewStepQuestionsQuery["complianceProgram"],
  assessmentTemplateId: number,
) => ({
  performance:
    complianceProgram?.performanceAssessmentTemplate?.id ===
    assessmentTemplateId,
  manager:
    complianceProgram?.managerAssessmentTemplate?.id === assessmentTemplateId,
  peer: complianceProgram?.peerAssessmentTemplate?.id === assessmentTemplateId,
  engagementSurvey:
    complianceProgram?.engagementSurveyTemplate?.id === assessmentTemplateId,
});

export const buildReviewSectionRows = (
  link: Pick<ReviewQuestionSetLink, "removed" | "questionSet">,
  overrides: ReadonlyArray<ReviewQuestionOverride>,
): ReviewSectionRow[] => {
  const overrideByQuestionId = new Map(
    overrides.map((override) => [override.questionId, override]),
  );
  return assertEdgesNonNull(link.questionSet.sections).map((section) => {
    const questions = assertEdgesNonNull(section.questions).map(
      ({ question }) => {
        const override = overrideByQuestionId.get(question.id);
        const hasOwnTitle = override?.title != null;
        const hasOwnDescription = override?.description != null;
        return {
          questionId: question.id,
          title: hasOwnTitle ? (override?.title ?? "") : question.title,
          description: hasOwnDescription
            ? override?.description
            : (question.description ?? null),
          isTalentIndicator:
            question.questionType === AssessmentQuestionType.TalentIndicator,
          removed: link.removed || !!override?.hidden,
          changed: hasOwnTitle || hasOwnDescription,
        };
      },
    );
    return {
      sectionId: section.pk ?? section.id,
      title: section.title ?? null,
      description: section.description ?? null,
      removed:
        link.removed ||
        (questions.length > 0 && questions.every(({ removed }) => removed)),
      questions,
    };
  });
};

/**
 * What a published review step changed, for the Question Preview: the questions its
 * reviewers no longer see, its own wording, and its removed talent indicators.
 */
export const reviewPreviewChanges = (
  template: Pick<
    ReviewStepQuestionsFragment,
    "questionSetLinks" | "questionOverrides"
  >,
): PreviewReviewChanges => {
  const hiddenQuestionIds = new Set(
    template.questionOverrides
      .filter(({ hidden }) => hidden)
      .map(({ questionId }) => questionId),
  );
  const linkQuestions = (link: ReviewQuestionSetLink) =>
    assertEdgesNonNull(link.questionSet.sections).flatMap((section) =>
      assertEdgesNonNull(section.questions).map(({ question }) => question),
    );
  const talentIndicatorsLink = template.questionSetLinks.find(
    ({ isTalentIndicators }) => isTalentIndicators,
  );
  template.questionSetLinks
    .filter(({ removed, isTalentIndicators }) => removed && !isTalentIndicators)
    .forEach((link) =>
      linkQuestions(link).forEach(({ id }) => hiddenQuestionIds.add(id)),
    );
  return {
    hiddenQuestionIds,
    wordingByQuestionId: new Map(
      template.questionOverrides
        .filter(
          ({ title, description }) => title != null || description != null,
        )
        .map(({ questionId, title, description }) => [
          questionId,
          { title: title ?? null, description: description ?? null },
        ]),
    ),
    talentIndicatorsRemoved: !!talentIndicatorsLink?.removed,
    hiddenTalentIndicatorIds: new Set(
      talentIndicatorsLink
        ? linkQuestions(talentIndicatorsLink).flatMap((question) =>
            hiddenQuestionIds.has(question.id) &&
            "talentIndicator" in question &&
            question.talentIndicator
              ? [question.talentIndicator.id]
              : [],
          )
        : [],
    ),
  };
};

/** What "Fix everywhere" changes besides this review, in one sentence. */
export const describeQuestionReach = (
  {
    otherReviewCount,
    inLibrary,
  }: {
    otherReviewCount: number;
    inLibrary: boolean;
  },
  noun: string,
) => {
  const library = "your question library";
  if (otherReviewCount === 0) {
    return inLibrary
      ? `This question is in ${library}. The new wording will show in every ${noun} that uses this question, including future ${noun}s.`
      : `No other ${noun} uses this question.`;
  }
  const reviews =
    otherReviewCount === 1
      ? `1 other ${noun}`
      : `${otherReviewCount} other ${noun}s`;
  return inLibrary
    ? `This question is also used in ${reviews} and in ${library}. The new wording will show in all of them, including future ${noun}s.`
    : `This question is also used in ${reviews}. The new wording will show there too.`;
};
